import zhCN from '../locales/zh-CN.json'
import en from '../locales/en.json'

export const DEFAULT_LANGUAGE = 'zh-CN'
export const LANGUAGE_STORAGE_KEY = 'siteLanguage'

const packs = {
  'zh-CN': zhCN,
  en
}

const textSources = new WeakMap()
const translatedValues = new Map()
let observer = null
let observerTimer = null

Object.entries(en.translations || {}).forEach(([source, translated]) => {
  translatedValues.set(translated, source)
})

function getPack(language) {
  return packs[language] || packs[DEFAULT_LANGUAGE]
}

function hasChinese(value) {
  return /[\u4e00-\u9fff]/.test(value)
}

function getFallbackEnglish(value) {
  const fallback = value
    .replace(/[\u4e00-\u9fff]+/g, '')
    .replace(/[，。、《》“”【】（）]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return fallback || 'Content'
}

function translateValue(value, language) {
  const source = translatedValues.get(value) || value

  if (language === DEFAULT_LANGUAGE) {
    return source
  }

  const translated = getPack(language).translations[source]

  if (translated) return translated

  return hasChinese(source) ? getFallbackEnglish(source) : source
}

export function translateText(value, language = getStoredLanguage()) {
  return translateValue(value, language)
}

function translateTextNode(node, language) {
  const raw = node.nodeValue
  const trimmed = raw.trim()

  if (!trimmed) return

  const cachedSource = textSources.get(node)
  const cachedTranslated = cachedSource ? translateValue(cachedSource, language) : ''
  const source = cachedSource && (trimmed === cachedSource || trimmed === cachedTranslated)
    ? cachedSource
    : translatedValues.get(trimmed) || trimmed

  textSources.set(node, source)

  const translated = translateValue(source, language)
  const leading = raw.match(/^\s*/)[0]
  const trailing = raw.match(/\s*$/)[0]
  const nextValue = `${leading}${translated}${trailing}`

  if (node.nodeValue !== nextValue) {
    node.nodeValue = nextValue
  }
}

function translateElementAttributes(element, language) {
  const translatableAttributes = ['alt', 'title', 'aria-label', 'placeholder']

  translatableAttributes.forEach(attr => {
    const value = element.getAttribute(attr)

    if (!value) return

    const sourceAttr = `data-i18n-source-${attr}`
    const source = element.getAttribute(sourceAttr) || translatedValues.get(value) || value
    const translated = translateValue(source, language)

    element.setAttribute(sourceAttr, source)

    if (value !== translated) {
      element.setAttribute(attr, translated)
    }
  })
}

function shouldSkipElement(element) {
  return ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(element.tagName)
}

export function applyLanguage(language = getStoredLanguage()) {
  const normalizedLanguage = packs[language] ? language : DEFAULT_LANGUAGE
  const root = document.getElementById('app')

  document.documentElement.lang = normalizedLanguage === 'en' ? 'en' : DEFAULT_LANGUAGE

  if (!root) return

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE && shouldSkipElement(node)) {
          return NodeFilter.FILTER_REJECT
        }

        return NodeFilter.FILTER_ACCEPT
      }
    }
  )

  let node = walker.currentNode

  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      translateTextNode(node, normalizedLanguage)
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      translateElementAttributes(node, normalizedLanguage)
    }

    node = walker.nextNode()
  }
}

export function getStoredLanguage() {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  return packs[stored] ? stored : DEFAULT_LANGUAGE
}

export function setStoredLanguage(language) {
  const normalizedLanguage = packs[language] ? language : DEFAULT_LANGUAGE

  localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage)
  applyLanguage(normalizedLanguage)
  window.dispatchEvent(new CustomEvent('site-language-change', { detail: normalizedLanguage }))
}

export function initLocalLanguage(router) {
  applyLanguage(getStoredLanguage())
  startLanguageObserver()

  if (router) {
    router.afterEach(() => {
      window.setTimeout(() => applyLanguage(getStoredLanguage()), 0)
    })
  }
}

function startLanguageObserver() {
  const root = document.getElementById('app')

  if (!root || observer) return

  observer = new MutationObserver(() => {
    if (getStoredLanguage() === DEFAULT_LANGUAGE) return

    window.clearTimeout(observerTimer)
    observerTimer = window.setTimeout(() => applyLanguage(getStoredLanguage()), 30)
  })

  observer.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['alt', 'title', 'aria-label', 'placeholder']
  })
}
